import {Plural, t, Trans} from '@lingui/macro';
import React, {useCallback, useMemo, useState} from 'react';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {SettingsResourcePagesEnum} from '../../models/url/settingsUrlModel';
import {getCompany} from '../../store/data/company/companySelectors';
import {SettingsContentHeader} from '../common/settingsContentHeader';
import {StyledSettingsContent, StyledSettingsMain, StyledSettingsWrapper} from '../common/settingsWrapper';
import {useFetchCircles} from '../fetchers/circles';
import {CircleMutationsEnum} from '../fetchers/circlesMutations';
import {SettingsHeader} from '../settingsHeader';
import {useCircleNavigateToLink} from './circlesHelpers';
import {useCirclesListInfoBox} from './circlesListInfoBox';
import {CirclesTable} from './circlesTable';

/*
 * Props.
 */

interface CirclesListProps {}

/*
 * Component.
 */

export const CirclesList: React.FC<CirclesListProps> = () => {
  const [search, setSearch] = useState('');
  const {circles, loadMore, moreStatus, mutate} = useFetchCircles(search);
  const {infoBox, infoBoxIcon} = useCirclesListInfoBox();
  const company = useAppSelector(getCompany);

  const [selectedCircleIds, setSelectedCircleIds] = useState<ReadonlyArray<number>>([]);

  const navigateTo = useCircleNavigateToLink();
  const localize = useLocalize();

  const renderCreateButton = useCallback(
    () => (
      <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => navigateTo(SettingsResourcePagesEnum.NEW)}>
        <Trans id="settings-circles-add">Add group</Trans>
      </Button>
    ),
    [navigateTo],
  );

  const onDeleteCircles = useCallback(() => {
    mutate({type: CircleMutationsEnum.DELETE, payload: {circleIds: selectedCircleIds, company}});
    setSelectedCircleIds([]);
  }, [company, mutate, selectedCircleIds]);

  const {deleteDialog, onDeleteWithDialog} = useBulkDeleteDialog(onDeleteCircles, selectedCircleIds.length);

  return (
    <StyledSettingsWrapper>
      {deleteDialog}
      <SettingsHeader right={infoBoxIcon}>
        <Trans id="settings-circles-list-title">Groups</Trans>
      </SettingsHeader>
      <StyledSettingsMain>
        <StyledSettingsContent>
          <SettingsContentHeader
            onExitClick={() => setSearch('')}
            onSearchInput={setSearch}
            searchInputValue={search}
            renderButtons={renderCreateButton}
            placeholder={localize(t('circle-list-search-placeholder')`Search group name`)}
          />
          <CirclesTable
            circles={circles}
            moreStatus={moreStatus}
            onLoadMore={loadMore}
            onDeleteCircles={onDeleteWithDialog}
            selectedCircleIds={selectedCircleIds}
            onSelectedCircleIds={setSelectedCircleIds}
          />
        </StyledSettingsContent>
        {infoBox}
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};

/*
 * Helpers.
 */

function useBulkDeleteDialog(onDelete: Handler, size: number) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const maybeDeleteDialog = useMemo(() => {
    if (!showDeleteDialog) {
      return null;
    }

    return (
      <OkCancelDialog
        title={
          <Plural
            id="settings-circle-list-dialog-delete-title"
            value={size}
            one="Delete 1 Group"
            other="Delete # Groups"
          />
        }
        okButtonLabel={<Trans id="settings-circle-list-dialog-delete-ok">Delete permanently</Trans>}
        onOkClick={() => {
          setShowDeleteDialog(false);
          onDelete();
        }}
        onRequestClose={() => setShowDeleteDialog(false)}
        okButtonVariant={ButtonVariantsEnum.DANGER}
      >
        <Plural
          id="settings-circle-list-dialog-delete-content-1"
          value={size}
          one="1 group will be permanently deleted in Front."
          other="# groups will be permanently deleted in Front."
        />
        <br />
        <Trans id="settings-circle-list-dialog-delete-content-2">This cannot be undone.</Trans>
      </OkCancelDialog>
    );
  }, [onDelete, showDeleteDialog, size]);

  const onDeleteWithDialog = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  return {deleteDialog: maybeDeleteDialog, onDeleteWithDialog};
}
